/**
 * Mandate Browser — Search and browse regulatory mandates (CFR sections).
 */
import React, { useState, useEffect } from 'react';
import InfoTooltip from '../../components/InfoTooltip';

const API = process.env.REACT_APP_API_URL;

export default function MandateBrowser() {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [partFilter, setPartFilter] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = search ? `${API}/api/ppm/mandates?search=${encodeURIComponent(search)}` : `${API}/api/ppm/mandates`;
        const res = await fetch(url, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setMandates(data.mandates || []);
      } catch {}
      setLoading(false);
    })();
  }, [search]);

  const filtered = partFilter ? mandates.filter(m => m.part === partFilter) : mandates;

  const partCounts = mandates.reduce((acc, m) => { acc[m.part || 'Other'] = (acc[m.part || 'Other'] || 0) + 1; return acc; }, {});

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-800 tracking-tight">Regulatory Mandates</h2>
            <InfoTooltip
              title="Regulatory Mandates"
              width={380}
              content={
                <>
                  The <span className="text-white font-medium">atomic regulatory requirements</span> your
                  policies have to satisfy — individual CFR sections (e.g., 42 CFR 422.503(b)(4)(vi)).
                  Every policy in your library links to one or more mandates, which is what
                  powers Coverage, Conflicts, and Drift.
                </>
              }
            />
          </div>
          <p className="text-slate-400 text-xs mt-0.5">{mandates.length} CFR sections tracked</p>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by CFR section or title..."
            className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl">
            {['', 'C', 'D', 'C/D'].map(p => (
              <button key={p} onClick={() => setPartFilter(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  partFilter === p ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                {p || 'All'} {partCounts[p || 'All'] ? '' : ''}{p ? ` (${partCounts[p] || 0})` : ''}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="text-slate-400 text-sm py-8">Loading mandates...</div>}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
            No mandates match your search.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-48">CFR Section</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide">Title</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-20">Part</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide w-24">Authority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-5">
                      <span className="font-mono text-xs text-blue-600 font-medium">{m.cfr_section}</span>
                    </td>
                    <td className="py-3 px-5 text-slate-700">{m.title}</td>
                    <td className="py-3 px-5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                        m.part === 'C' ? 'bg-violet-50 text-violet-700 border-violet-200' :
                        m.part === 'D' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                        'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>{m.part || '—'}</span>
                    </td>
                    <td className="py-3 px-5 text-xs text-slate-500">{m.authority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
